/**
 * Outbox + realtime contract (doc 04 · Realtime).
 *
 * Every mutation writes its domain rows and one `events` row in the same transaction. A relay
 * forwards those rows to Redis pub/sub; each api process pushes them into the Socket.IO rooms
 * below. The type strings are ours (monday has no equivalent public vocabulary) and are the
 * same strings automation triggers subscribe to.
 */
import { z } from 'zod';
import { bigIntIdSchema } from './ids.js';
export const EVENT_TYPES = [
    /** Workspaces and folders (M1-B1 §9). The sidebar tree patches on these. */
    'workspace.created',
    'workspace.updated',
    'workspace.deleted',
    'folder.created',
    'folder.updated',
    'folder.deleted',
    'board.created',
    'board.updated',
    'board.deleted',
    'board.moved',
    'board.archived',
    'board.restored',
    'group.created',
    'group.updated',
    /** A reorder or a rebalance (M1-B2 §5.6); `payload.rebalanced` says which. */
    'group.moved',
    'group.deleted',
    'column.created',
    'column.updated',
    'column.moved',
    'column.deleted',
    'item.created',
    'item.updated',
    'item.moved',
    'item.deleted',
    'cell.changed',
    'update.created',
    'update.deleted',
    'view.created',
    'view.updated',
    'view.deleted',
    /** Account identity changes (M0-F3 §5.12). They fan out to `user:<id>` and, for role/status
     * changes, to every board room the user subscribes to, so clients drop cached permissions. */
    'user.created',
    'user.updated',
    'user.status_changed',
    'user.role_changed',
    /** M5 notifications. All three carry `userId` = the recipient, which is what routes them to
     * `user:<id>` and drives the bell badge with no polling (M5 §4.1). */
    'notification.created',
    'notification.read',
    'notification.cleared',
    /**
     * M5.C3 — the **Update feed's** read state changed for `userId` (P-283). A different store
     * from the bell, so a different type: `notification.read` must not be made to mean both, or
     * one badge invalidates the other's query. Published straight onto the bus, never through the
     * outbox — no automation trigger subscribes to it.
     */
    'inbox.changed',
    /**
     * M7 dashboards and widgets. They carry `dashboardId` (routing to `dashboard:<id>`) when the
     * canvas is a standalone dashboard, and `boardId` when it is a board's widget view — which is
     * the room its viewers are already in (`specs/M10-D1-dashboards.md` §6.2).
     */
    'dashboard.created',
    'dashboard.updated',
    'dashboard.deleted',
    'widget.created',
    'widget.updated',
    'widget.moved',
    'widget.deleted',
    /**
     * M10 Tasks (round T1). One event **per affected user** — the assignee and the assigner —
     * carrying `userId` and a null `boardId`, so it routes to `user:<id>` only and never into a
     * board room. The board is named inside the payload; the permission rule that decides who may
     * see the task is re-applied by `tasks.list`, never by the room membership
     * (`specs/M10-tasks.md` §6).
     */
    'task.changed',
    /**
     * M6-A1 / P-310 — an automation **definition** changed: created, edited, toggled or deleted.
     *
     * It carries nothing but `boardId` and the payload below, and it exists for one consumer: the
     * worker's matching index, which is a cache rebuilt from the database (R3). Before this event
     * the only thing that refreshed it was the `AUTOMATION_INDEX_REFRESH_MS` tick, so an
     * automation created at 13:43:03 did not fire for an event at 13:43:25 and one switched off a
     * minute ago still did. No automation trigger subscribes to it — a recipe cannot be triggered
     * by another recipe being saved — and the browser ignores it: the automations list keeps
     * patching on the `board.updated` event these same mutations still emit.
     */
    'automation.changed',
];
export const eventTypeSchema = z.enum(EVENT_TYPES);
export const eventEnvelopeSchema = z.object({
    id: bigIntIdSchema,
    type: eventTypeSchema,
    boardId: bigIntIdSchema.nullable().default(null),
    itemId: bigIntIdSchema.nullable().default(null),
    /** Who caused it; null for system/scheduler events. */
    actorId: bigIntIdSchema.nullable().default(null),
    /**
     * The **subject** user, not the actor (F5 §4.1): what routes an event to `user:<id>`.
     * A deactivation is `userId` = the person deactivated, `actorId` = the admin who did it.
     */
    userId: bigIntIdSchema.nullable().default(null),
    /** Routes to `dashboard:<id>` (M7 fills it). */
    dashboardId: bigIntIdSchema.nullable().default(null),
    /**
     * The new `updated_at` of the entity the patch describes, ISO. The client drops a patch
     * older than what it already holds, per entity, with no rule per event type (F5 §5.3) —
     * which is what keeps the api relay and the worker's outbox relay from fighting.
     */
    entityUpdatedAt: z.string().nullable().default(null),
    payload: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.string(),
    /**
     * Client-supplied id of the optimistic mutation that produced this event, so the browser can
     * reconcile its own optimistic patch instead of applying it twice.
     */
    mutationId: z.string().nullable().default(null),
    /** Automation loop protection: causal chain id + depth (doc 04 · Automation engine). */
    chainId: z.string().nullable().default(null),
    chainDepth: z.number().int().nonnegative().default(0),
});
/** Socket.IO room names. Keep in one place so client and server can never drift. */
export const rooms = {
    board: (boardId) => `board:${boardId}`,
    item: (itemId) => `item:${itemId}`,
    user: (userId) => `user:${userId}`,
    dashboard: (dashboardId) => `dashboard:${dashboardId}`,
};
/* ── `cell.changed` (P-278) ───────────────────────────────────────────────── */
/**
 * **One** payload shape for `cell.changed`, whatever wrote the cell (P-278).
 *
 * Before this schema existed each producer built the record by hand — `cells.setCellValue`,
 * `cells.setBatch`/`bulkSet`, the button counter, the timer, the formula/mirror recompute and
 * the subtasks recompute — and they had already drifted: the subtasks one carried neither
 * `boardId` nor `columnType` nor `batchId`. Everything downstream of the outbox (the automation
 * engine's triggers, the webhook fan-out, the search indexer, the client's cell patch) reads
 * those keys, so a producer that omits one silently changes what a consumer sees. A batch write
 * differs from a single write in **exactly one key**, `batchId`, and this schema is what makes
 * that true by construction rather than by review.
 *
 * `value` / `previousValue` are `unknown` **on purpose**: the shape is the column type's own
 * (`packages/shared/src/columns/*`), so it is an object for status (`{index}`), date, people,
 * dropdown, timeline … and a bare string or number for others. Nothing between the column
 * registry and the consumer may narrow it.
 */
export const cellChangedPayloadSchema = z.object({
    boardId: z.string(),
    itemId: z.string(),
    columnId: z.string(),
    columnType: z.string(),
    /** monday's own type name (`color`, `duration`, …); null when the type has none. */
    legacyColumnType: z.string().nullable().default(null),
    value: z.unknown(),
    previousValue: z.unknown(),
    text: z.string().nullable().default(null),
    previousText: z.string().nullable().default(null),
    /**
     * The `action_record_uuid` of the multi-cell action this write belonged to — a mass edit
     * (`cells.bulkSet`) or a range paste/clear (`cells.setBatch`) — and `null` for a single write.
     * It is a **label**, never a switch: no consumer may treat a batch event as a different kind
     * of event, which is the defect P-278 filed.
     */
    batchId: z.string().nullable().default(null),
    isUndo: z.boolean().default(false),
    /** A recomputation (formula, mirror, subtasks): no actor, no undo entry, no activity row. */
    computed: z.boolean().default(false),
});
/**
 * The only constructor of a `cell.changed` payload. Every producer calls it, so single, bulk
 * and batch writes cannot drift apart again (P-278).
 */
export function cellChangedPayload(input) {
    return cellChangedPayloadSchema.parse({
        ...input,
        boardId: String(input.boardId),
        itemId: String(input.itemId),
    });
}
/* ── `automation.changed` (P-310) ─────────────────────────────────────────── */
/**
 * What happened to the definition. The four are what the service layer can do to one row:
 * `toggled` covers both directions of the card switch, because the index does not care which
 * way it went — it reloads the board and re-reads `active AND state = 'active'` either way.
 */
export const AUTOMATION_CHANGES = ['created', 'updated', 'toggled', 'deleted'];
/**
 * The payload of `automation.changed`. `active`/`state` are informational — a consumer that
 * acted on them would be trusting an event instead of the database, which is exactly the
 * mistake R3 forbids: the index reloads the **board** from SQL and never patches one entry
 * from what an event said.
 */
export const automationChangedPayloadSchema = z.object({
    automationId: bigIntIdSchema,
    change: z.enum(AUTOMATION_CHANGES),
    active: z.boolean().nullable().default(null),
    state: z.string().nullable().default(null),
});
/** The only constructor, so the four call sites in the service cannot drift. */
export function automationChangedPayload(input) {
    return automationChangedPayloadSchema.parse({
        ...input,
        automationId: String(input.automationId),
    });
}
/** Redis pub/sub channel the relay publishes every outbox row on. */
export const EVENT_CHANNEL = 'elizmonday:events';
/**
 * Control channel (F5 §5.4): out-of-band messages that are **not** outbox events — they carry
 * no domain change, are never replayed and never reach the automation engine. F3 and F8 use
 * them to invalidate caches and to disconnect revoked sessions across api processes.
 */
export const CONTROL_CHANNEL = 'elizmonday:control';
export const CONTROL_MESSAGE_TYPES = [
    /** A board's access surface changed: drop `boardAccess:*:<boardId>:*` and re-run `canJoin`. */
    'permissions.changed',
    /** A user's role / status / team membership changed: drop `actor:<userId>`. */
    'actor.changed',
    /** One or more sessions were revoked: disconnect that user's live sockets. */
    'session.revoked',
    /**
     * The account block changed — brand colours, product name, wordmark, logo, the capability
     * matrix (M8-P2 D1). Every connected socket is told, whoever they are, so an open tab
     * re-reads `shell.bootstrap` and repaints **without a reload**.
     */
    'account.changed',
];
export const controlMessageSchema = z.object({
    type: z.enum(CONTROL_MESSAGE_TYPES),
    boardId: z.string().nullable().default(null),
    userId: z.string().nullable().default(null),
    sessionId: z.string().nullable().default(null),
    at: z.string().default(() => new Date().toISOString()),
});
/** Socket.IO event names (F5 §5.1, §5.5, §5.7, §5.8). */
export const SOCKET_EVENTS = {
    /** client -> server */
    joinBoard: 'board:join',
    leaveBoard: 'board:leave',
    presenceEnter: 'presence:enter',
    presenceHeartbeat: 'presence:heartbeat',
    presenceCell: 'presence:cell',
    presenceLeave: 'presence:leave',
    /** server -> client */
    event: 'event',
    joined: 'joined',
    presence: 'presence',
    /** The actor lost read access to a room they were in; the client shows the no-access panel. */
    roomRevoked: 'room:revoked',
    /** The per-socket outbound queue overflowed; the client discards and re-syncs (§5.8). */
    resyncRequired: 'resync-required',
    /** M8-P2 D1: the account's branding or capability matrix changed; re-read the bootstrap. */
    accountChanged: 'account:changed',
};
/* ── presence (F5 §5.7) ───────────────────────────────────────────────────── */
/** How often a live client refreshes its roster entry; the TTL is three times this. */
export const PRESENCE_HEARTBEAT_MS = 15_000;
/** Roster entry TTL: a crashed browser disappears without any explicit leave. */
export const PRESENCE_TTL_MS = 45_000;
/** A cell "someone is editing here" marker; advisory only, never a lock (§5.7, OQ-F5-3). */
export const PRESENCE_CELL_TTL_MS = 10_000;
/** The roster is broadcast to a board at most this often, however many heartbeats arrive. */
export const PRESENCE_BROADCAST_INTERVAL_MS = 1_000;
/** Entries carried in a roster message; `total` always carries the true count. */
export const PRESENCE_ROSTER_LIMIT = 50;
/* ── backpressure and payload limits (F5 §5.8) ────────────────────────────── */
/** Above this an envelope is replaced by an id-only patch and the client refetches. */
export const MAX_ENVELOPE_BYTES = 16 * 1024;
/** `cell.changed` for the same `(itemId, columnId)` collapses to the last one in this window. */
export const COALESCE_WINDOW_MS = 100;
/** A bulk mutation is split into batches of this size. */
export const BATCH_SPLIT = 500;
/** Per-socket outbound queue caps; on overflow the socket is told to re-sync. */
export const SOCKET_QUEUE_LIMIT = 2_000;
/** The seen-set the client keeps so an at-least-once duplicate changes the store once. */
export const SEEN_EVENT_LIMIT = 2_000;
/**
 * Envelope ids are decimal strings of bigints `[ADR-002 §2]`, and `"1000000000010"` sorts
 * **before** `"1000000000009"` as a string. Every comparison goes through here.
 */
export function compareEventIds(a, b) {
    const left = BigInt(a);
    const right = BigInt(b);
    return left < right ? -1 : left > right ? 1 : 0;
}
/**
 * Is `incoming` stale for an entity the client already holds at `known`? Both are ISO
 * timestamps; a missing one is never stale, because "we do not know" must not drop a patch.
 */
export function isStalePatch(incoming, known) {
    if (!incoming || !known)
        return false;
    return Date.parse(incoming) < Date.parse(known);
}
//# sourceMappingURL=events.js.map